// KAI — the one-syllable private-label brand page. Brand name comes from
// shopCatalog.BRAND so a rename (e.g. to VAE) is a one-line change.
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Droplets, Leaf, PackageCheck } from "lucide-react";
import { Reveal } from "@/components/motion/primitives";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";
import { BRAND, SHOP_CATEGORIES } from "@/lib/shopCatalog";

const STEPS = [
  { icon: Leaf, title: "Curated formulas", desc: "Every product starts from a proven base — skincare, home care, textiles — developed with specialist manufacturers in Türkiye, one of Europe's oldest textile and cosmetics regions." },
  { icon: Droplets, title: "One name, one standard", desc: `Everything ships under a single syllable: ${BRAND.name}. One label, one quality bar, whether it's a face cream, a bathrobe or a laundry detergent.` },
  { icon: PackageCheck, title: "Shipped straight to you", desc: "Orders are picked, packed and dropshipped directly from the fulfillment line — no warehouse middlemen, no stale stock." },
];

export default function Kai() {
  return (
    <div data-testid="kai-page">
      <SEO
        title={`${BRAND.name} — ${BRAND.tagline} | Julius Mason`}
        description={`${BRAND.name} is Julius Mason's one-syllable private-label brand: skincare, personal care, home cleaning, hotel amenities, textiles and more — one label, one standard, shipped direct.`}
        path={`/${BRAND.slug}`}
        lang="en"
        keywords={`${BRAND.name} brand, private label essentials, skincare, home cleaning, hotel amenities`}
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: BRAND.name, path: `/${BRAND.slug}` },
        ])}
      />

      {/* Hero — pure typography, no stock gloss */}
      <section className="bg-[#FAF7F2] text-[#141414]" data-testid="kai-hero">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 pt-32 md:pt-40 pb-24 md:pb-32">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#C96F3B] mb-6">Private label · Est. 2026</p>
            <h1 className="font-display leading-[0.85] tracking-tight text-[clamp(5rem,18vw,13rem)]" data-testid="kai-wordmark">{BRAND.name}</h1>
            <p className="font-display text-2xl md:text-4xl mt-6 text-[#141414]/90">{BRAND.tagline}</p>
            <p className="mt-6 max-w-xl text-[#141414]/60 leading-relaxed">
              One syllable you can say in any language, on everything a home actually needs —
              from the face cream by the sink to the towel on the rail and the detergent under it.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="kai-shop-cta"
                className="inline-flex items-center gap-2 bg-[#C96F3B] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#b35f2f] transition active:scale-95">
                Browse the range <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#kai-ranges" data-testid="kai-ranges-cta"
                className="inline-flex items-center gap-2 border border-[#141414]/25 px-7 py-3.5 text-sm font-semibold hover:border-[#141414]/70 transition">
                Explore categories
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24 bg-white border-t border-[#141414]/10">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 grid md:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#C96F3B] mb-4">The idea</p>
            <h2 className="font-display text-3xl md:text-4xl mb-6 text-[#141414]">Why one syllable?</h2>
            <div className="space-y-4 text-[#141414]/70 leading-relaxed">
              <p>
                Big brands spend billions making you remember long names. {BRAND.name} bets on the opposite:
                the shortest possible name, on the widest possible shelf. If a product isn't good enough
                to carry three letters, it doesn't ship.
              </p>
              <p>
                The range is produced with private-label specialists in Türkiye and sold across Europe and
                beyond — beauty, home, hospitality. Same label everywhere, so you always know what
                you're getting: honest formulation, fair price, no theatre.
              </p>
            </div>
          </Reveal>
          <div className="space-y-6">
            {STEPS.map((s) => (
              <Reveal key={s.title}>
                <div className="flex gap-4 bg-white border border-[#141414]/10 p-6" data-testid="kai-step">
                  <s.icon className="w-6 h-6 text-[#C96F3B] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-[#141414] mb-1.5">{s.title}</h3>
                    <p className="text-sm text-[#141414]/60 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ranges — typographic tiles per category */}
      <section id="kai-ranges" className="py-16 md:py-24 bg-[#141414] text-[#FAF7F2]" data-testid="kai-ranges">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#C96F3B] mb-4">The ranges</p>
            <h2 className="font-display text-3xl md:text-4xl mb-3">Every aisle, one label.</h2>
            <p className="text-[#FAF7F2]/60 max-w-2xl mb-12">
              New lines land as they're imported — each tile opens that aisle in the shop.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#FAF7F2]/10 border border-[#FAF7F2]/10">
            {SHOP_CATEGORIES.map((c) => (
              <Link key={c.id} to={`/shop?cat=${c.id}`} data-testid={`kai-range-${c.id}`}
                className="group bg-[#141414] p-8 hover:bg-[#1c1c1c] transition-colors">
                <p className="font-display text-5xl text-[#C96F3B]/25 group-hover:text-[#C96F3B]/60 transition-colors mb-6">
                  {c.label.charAt(0)}
                </p>
                <h3 className="font-display text-xl mb-2">{c.label}</h3>
                <p className="text-sm text-[#FAF7F2]/50 leading-relaxed mb-4">{c.blurb}</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#C96F3B] opacity-0 group-hover:opacity-100 transition-opacity">
                  Shop this range <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#FAF7F2] text-center">
        <div className="mx-auto max-w-2xl px-6">
          <h2 className="font-display text-3xl md:text-4xl mb-4 text-[#141414]">Three letters. That's the whole pitch.</h2>
          <p className="text-[#141414]/60 mb-8">Secure checkout via Shopify, direct fulfillment, new stock imported continuously.</p>
          <Link to="/shop" data-testid="kai-bottom-cta"
            className="inline-flex items-center gap-2 bg-[#141414] text-[#FAF7F2] px-8 py-4 text-sm font-semibold hover:bg-[#C96F3B] transition active:scale-95">
            Shop {BRAND.name} now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
